// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import {
    OrderLineModel,
    OrderModel,
    SessionModel,
    WishListSettingsModel,
} from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import VmiBinDetailTypeLink from "@insite/content-library/Components/VmiBinDetailTypeLink";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer, {
    CartLineContainerStyles,
    LineHeaderStyles,
    LinesHeader,
} from "@plumberssupply/App/Components/Cart/CartContainerLayout";
import cartLineSharedStyles from "@plumberssupply/App/SharedStyle/Cart/CartLineSharedStyles";
import { xPartNumberStyle } from "@plumberssupply/App/SharedStyle/ProductPartNumberStyle";
import { productPriceStyles } from "@plumberssupply/Overrides/Components/ProductPrice";
import { cartLineQuantityStyles } from "@plumberssupply/Overrides/Widgets/Cart/CartLineQuantity";
import {
    avoidBreakInsidePrintElement,
    displayBlockOnPrint,
    parseBoolean,
    xScreen,
} from "@plumberssupply/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    vmiBinDetailsPageUrl: getPageLinkByPageType(state, "VmiBinDetailsPage")?.url,

    // XNG-change
    session: getSession(state),
    canSeeListPrice: parseBoolean(getSession(state).properties.canSeeListPrice),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    orderLineCardImage?: LazyImageProps;
    productInfoTitle?: SmallHeadingAndTextStyles;
    productBrandStyles?: ProductBrandStyles;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoSectionOptionsText?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    // XNG-Changes
    price?: TypographyPresentationProps;
    listPriceText?: TypographyPresentationProps;
    listPriceLabel?: TypographyPresentationProps;
    totalPrice?: TypographyPresentationProps;
    unitOfMeasure?: TypographyPresentationProps;
    qtyItemsShipped?: TypographyPresentationProps;
    notes?: TypographyPresentationProps;
    addToListLink?: LinkPresentationProps;
    qtyStyles?: TextFieldPresentationProps;
    qtyTextPrint?: TypographyPresentationProps;
    cartLineSection?: CartLineContainerStyles;
    productBrandAndDescriptionGridItem?: InjectableCss;
    priceWrapper?: InjectableCss;
    linesTitleTypography?: TypographyProps;
    linesHeader?: LineHeaderStyles;
    productInfoTitleWrapper?: InjectableCss;
}

// XNG-Changes
export const orderLinesListStyles: OrderLinesListStyles = {
    gridContainer: {
        gap: 10,
    },
    linesHeader: {
        wrapper: {
            css: css`
                @media print {
                    &&&& {
                        display: flex;
                    }
                }
            `,
        },
        rightSection: {
            css: css`
                @media print {
                    display: flex;
                }
            `,
        },
        qty: {
            css: css`
                @media print {
                    text-align: center;
                }
            `,
        },
        itemDescription: {
            css: css`
                @media print {
                    max-width: 50%;
                }
            `,
        },
    },
    productInfoTitle: {
        extendedStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    flex-direction: row;
                    margin-bottom: 4px;
                    @media print {
                        margin-right: 8px;
                    }
                `,
            },
            heading: {
                ...xPartNumberStyle.label,
            },
            text: {
                ...xPartNumberStyle.value,
            },
        },
    },
    productInfoTitleWrapper: {
        css: css`
            @media print {
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
            }
        `,
    },
    productBrandStyles: {
        nameText: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
        nameLink: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    productBrandAndDescriptionGridItem: { ...cartLineSharedStyles.productBrandAndDescriptionGridItem },
    price: { ...cartLineSharedStyles?.productPrice?.price?.priceText },
    totalPrice: { ...cartLineSharedStyles?.extendedUnitNetPriceText },
    listPriceText: { ...productPriceStyles?.price?.listPriceText },
    listPriceLabel: { ...productPriceStyles?.price?.listPriceLabel },
    unitOfMeasure: {
        ...cartLineSharedStyles?.productPrice?.price?.unitOfMeasure?.unitOfMeasureText,
    },
    qtyStyles: {
        ...cartLineQuantityStyles.quantityOrderedTextField,
        cssOverrides: {
            ...cartLineQuantityStyles.quantityOrderedTextField?.cssOverrides,
            formField: css`
                ${cartLineQuantityStyles.quantityOrderedTextField?.cssOverrides?.formField}
                @media print {
                    display: none;
                }
            `,
        },
    },
    qtyTextPrint: {
        css: css`
            display: none;
            font-size: 14px;
            text-align: center;
            @media print {
                display: block;
            }
        `,
    },
    addToListLink: {
        ...cartLineSharedStyles.addToListLink,
    },
    qtyItemsShipped: {
        size: 12,
        lineHeight: "15px",
        css: css`
            padding-top: 5px;
            text-align: center;
            ${xScreen("lte", "md")} {
                padding-top: 10px;
            }
            @media print {
                padding-top: 0;
            }
        `,
    },
    cartLineSection: {
        container: {
            css: css`
                width: 100%;
                ${avoidBreakInsidePrintElement}
                @media print {
                    display: flex;
                    padding: 8px 0;
                }
            `,
        },
        peripheralActions: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
        lineNotesArea: {
            css: css`
                text-area {
                    background-color: #eeeeee;
                }
                &,
                div[class^="FormFieldStyle"],
                div[class^="FormInputWrapper"] {
                    ${avoidBreakInsidePrintElement}
                }
                @media print {
                    div[class^="FormFieldStyle"],
                    div[class^="FormInputWrapper"],
                    label {
                        display: block;
                        width: 100%;
                    }
                }
            `,
        },
        qtyPriceWrapper: {
            css: css`
                @media print {
                    width: 440px;
                    max-width: 50%;
                }
            `,
        },
        total: {
            css: css`
                @media print {
                    margin-left: 20px;
                }
            `,
        },
        infoSection: {
            css: css`
                ${avoidBreakInsidePrintElement}
                @media print {
                    min-width: unset;
                    width: 50%;
                    padding-right: 10px;
                }
            `,
        },
        image: {
            css: css`
                &,
                a,
                img {
                    ${avoidBreakInsidePrintElement}
                }
                @media print {
                    display: none;
                }
            `,
        },
        qty: {
            css: css`
                @media print {
                    margin-top: 0;
                    display: block;
                }
            `,
        },
        pricesWrapper: {
            css: css`
                @media print {
                    flex-direction: row;
                    margin-top: 0;
                    justify-content: center;
                }
            `,
        },
    },
    priceWrapper: {
        css: css`
            ${xScreen("lte", "md")} {
                margin-bottom: 9px;
            }
        `,
    },
    linesTitleTypography: {
        as: "h3",
        variant: "h3",
    },
};

const OrderLineProductInfo = ({
    orderLine,
    styles,
    vmiLocationId,
}: {
    orderLine: OrderLineModel;
    styles: OrderLinesListStyles;
    vmiLocationId: string;
}) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions && orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(option => (
            <li {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                <Typography {...styles.productInfoSectionOptionsText}>
                    {option.sectionName}: {option.optionName}
                </Typography>
            </li>
        ));
    }

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.productBrandAndDescriptionGridItem}>
                {orderLine.brand && <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrandStyles} />}

                {vmiLocationId && orderLine.isActiveVmiBin && (
                    <VmiBinDetailTypeLink
                        title={orderLine.shortDescription}
                        locationId={vmiLocationId}
                        id={orderLine.vmiBinId}
                    />
                )}
                {vmiLocationId && !orderLine.isActiveVmiBin && (
                    <Typography as="p">{orderLine.shortDescription}</Typography>
                )}
                {!vmiLocationId && (
                    <ProductDescription product={orderLine} extendedStyles={styles.productDescriptionStyles} />
                )}
            </StyledWrapper>
            {sectionOptions.length > 0 && (
                <StyledWrapper>
                    <ul {...styles.productInfoSectionOptionsList}>{sectionOptions}</ul>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.productInfoTitleWrapper}>
                {orderLine.customerProductNumber && (
                    <SmallHeadingAndText
                        {...styles.productInfoTitle}
                        heading={translate("My Part #")}
                        text={orderLine.customerProductNumber}
                    />
                )}

                {orderLine.manufacturerItem && (
                    <SmallHeadingAndText
                        {...styles.productInfoTitle}
                        heading={translate("MFG #")}
                        text={orderLine.manufacturerItem}
                    />
                )}
                <SmallHeadingAndText
                    {...styles.productInfoTitle}
                    heading={translate("Part #")}
                    text={orderLine.productErpNumber}
                />
                {orderLine.vmiBinNumber && (
                    <SmallHeadingAndText
                        {...styles.productInfoTitle}
                        heading={translate("Bin #")}
                        text={orderLine.vmiBinNumber}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const OrderLineCard = (props: {
    orderLine: OrderLineModel;
    order: OrderModel;
    session: SessionModel;
    wishListSettings?: WishListSettingsModel;
    vmiBinDetailsPageUrl?: string;
    styles: OrderLinesListStyles;
    enableVat: boolean;
    vatPriceDisplay: string;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    // XNG-Changes
    canSeeListPrice: boolean;
}) => {
    const {
        orderLine,
        order,
        wishListSettings,
        vmiBinDetailsPageUrl,
        styles,
        setAddToListModalIsOpen,
        addToWishList,
        session,
        enableVat,
        vatPriceDisplay,
        canSeeListPrice,
    } = props;
    const toasterContext = React.useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    // XNG-change on price functionality
    const canSeePrices =
        session.userRoles && session.userRoles.length > 0 ? !session.userRoles.includes("EmployeeWithoutPricing") : false;

    const isShareEntity = getIsShareEntity();

    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map(promotion => (
                <Typography {...styles.orderLineInfoPromotionText} as="li" key={promotion.id}>
                    {promotion.name}
                </Typography>
            ));
    }

    // XNG-Changes
    return (
        <CartLineContainer extendedStyles={styles.cartLineSection}>
            <CartLineContainer.Image>
                {orderLine.productUri && orderLine.isActiveProduct && !order.vmiLocationId && (
                    <Link href={orderLine.productUri}>
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    </Link>
                )}
                {orderLine.productUri && orderLine.isActiveProduct && order.vmiLocationId && orderLine.isActiveVmiBin && (
                    <Link href={`${vmiBinDetailsPageUrl}?locationId=${order.vmiLocationId}&id=${orderLine.vmiBinId}`}>
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    </Link>
                )}
                {(!orderLine.isActiveProduct || !orderLine.productUri) && (
                    <LazyImage
                        {...styles.orderLineCardImage}
                        src={orderLine.mediumImagePath}
                        isShareEntity={isShareEntity}
                    />
                )}
            </CartLineContainer.Image>
            <CartLineContainer.Info>
                <OrderLineProductInfo orderLine={orderLine} styles={styles} vmiLocationId={order.vmiLocationId} />
            </CartLineContainer.Info>
            <CartLineContainer.Qty>
                {orderLine.qtyOrdered && (
                    <TextField
                        value={orderLine.qtyOrdered}
                        disabled={true}
                        className="qty_readOnly"
                        {...styles.qtyStyles}
                    />
                )}
                <Typography {...styles.qtyTextPrint}>{orderLine.qtyOrdered}</Typography>

                <Typography {...styles.qtyItemsShipped}>
                    {translate(`${orderLine.qtyShipped} Item(s) Shipped`)}
                </Typography>
            </CartLineContainer.Qty>

            <CartLineContainer.Price>
                {canSeePrices && (
                    <>
                        {canSeeListPrice && (
                            <StyledWrapper>
                                <Typography {...styles.listPriceText}>{orderLine.unitListPriceDisplay}</Typography>
                                <Typography {...styles.listPriceLabel}>{translate("List Price")}</Typography>
                            </StyledWrapper>
                        )}

                        <StyledWrapper {...styles.priceWrapper}>
                            <Typography {...styles.price}>
                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? orderLine.unitPriceWithVatDisplay
                                    : orderLine.unitPriceDisplay}
                            </Typography>
                            <Typography as="span" {...styles.unitOfMeasure}>
                                {orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : ""}
                            </Typography>
                            {enableVat && (
                                <>
                                    <Typography as="p" {...styles.vatLabelText}>
                                        {vatPriceDisplay === "DisplayWithVat" ||
                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                            ? `${translate("Inc. VAT")} (${orderLine.taxRate}%)`
                                            : translate("Ex. VAT")}
                                    </Typography>
                                    {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <>
                                            <Typography {...styles.secondaryPriceText} as="p">
                                                {orderLine.unitPriceDisplay +
                                                    (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")}
                                            </Typography>
                                            <Typography as="p" {...styles.secondaryVatLabelText}>
                                                {translate("Ex. VAT")}
                                            </Typography>
                                        </>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                    </>
                )}
            </CartLineContainer.Price>

            <CartLineContainer.Total>
                {canSeePrices && (
                    <>
                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <Typography {...styles.totalPrice}>{orderLine.extendedUnitNetPriceDisplay}</Typography>
                        )}
                        {enableVat && vatPriceDisplay !== "DisplayWithoutVat" ? (
                            <Typography {...styles.price}>{orderLine.netPriceWithVatDisplay}</Typography>
                        ) : (
                            <Typography {...styles.totalPrice}>{orderLine.extendedUnitNetPriceDisplay}</Typography>
                        )}
                    </>
                )}
            </CartLineContainer.Total>

            <CartLineContainer.Configurations>
                {promotions.length > 0 && (
                    <StyledWrapper>
                        <ul {...styles.orderLineInfoPromotionList}>{promotions}</ul>
                    </StyledWrapper>
                )}
            </CartLineContainer.Configurations>
            <CartLineContainer.PeripheralActions>
                {orderLine.canAddToWishlist && (
                    <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                        {translate("Add to List")}
                    </Link>
                )}
            </CartLineContainer.PeripheralActions>
            <CartLineContainer.LineNotesArea>
                <>
                    {orderLine.notes && (
                        <TextArea
                            label={translate("Line Note")}
                            value={orderLine.notes}
                            disabled={true}
                            data-test-selector="cartline_notes"
                        />
                    )}
                </>
            </CartLineContainer.LineNotesArea>
        </CartLineContainer>
    );
};

const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    vmiBinDetailsPageUrl,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    session,
    enableVat,
    vatPriceDisplay,
    canSeeListPrice,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));

    // XNG-change on price functionality
    const canSeePrices = !session?.userRoles?.includes("EmployeeWithoutPricing");

    if (!order || !order.orderLines) {
        return null;
    }

    return (
        <GridContainer {...styles.gridContainer}>
            <Typography {...styles.linesTitleTypography}>{translate("Order Lines")}</Typography>
            {canSeePrices ? (
                <>
                    {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                        <LinesHeader
                            extendedStyles={styles.linesHeader}
                            rightTitles={["QTY", "Price/Unit", "total Ex. VAT"]}
                        />
                    )}
                    {!enableVat ? (
                        <LinesHeader extendedStyles={styles.linesHeader} />
                    ) : vatPriceDisplay !== "DisplayWithoutVat" ? (
                        <LinesHeader
                            extendedStyles={styles.linesHeader}
                            rightTitles={["QTY", "Price/Unit", "total Inc. VAT"]}
                        />
                    ) : (
                        <LinesHeader
                            extendedStyles={styles.linesHeader}
                            rightTitles={["QTY", "Price/Unit", "total Ex. VAT"]}
                        />
                    )}
                </>
            ) : (
                <LinesHeader extendedStyles={styles.linesHeader} rightTitles={["QTY"]} />
            )}

            {order.orderLines.map(orderLine => (
                <OrderLineCard
                    key={orderLine.id}
                    orderLine={orderLine}
                    order={order}
                    wishListSettings={wishListSettings}
                    vmiBinDetailsPageUrl={vmiBinDetailsPageUrl}
                    styles={styles}
                    setAddToListModalIsOpen={setAddToListModalIsOpen}
                    addToWishList={addToWishList}
                    canSeeListPrice={canSeeListPrice}
                    {...({ session } as any)}
                    {...({ enableVat } as any)}
                    {...({ vatPriceDisplay } as any)}
                />
            ))}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList);
